import type { WhereLeaf } from '../../../query-dsl';
import type { ColumnFilter, CustomFieldValueType } from '../types';
import { toFilterLeaf } from './build-filter-where';
import {
  CUSTOM_FIELD_COLUMN_PREFIX,
  type CustomFieldFilter,
  type CustomFieldValueColumn,
} from './custom-field-column';

/**
 * The metadata `resolveCustomFieldFilter` needs to translate a raw
 * `ColumnFilter` into the exact-condition `CustomFieldFilter` descriptor
 * `buildFilterWhere` expects. A consumer builds a
 * `ReadonlyMap<definitionId, CustomFieldFilterMeta>` from the same
 * `CustomFieldDefinitionInput[]` it already resolved for
 * `mergeCustomFieldColumns` (design.md D4) — this type intentionally does
 * NOT reuse `CustomFieldDefinitionInput` directly so this module never
 * depends on `merge-custom-field-columns.ts` (keeps the `lib/` dependency
 * graph acyclic; `build-filter-where.ts` already depends on
 * `custom-field-column.ts`, and this module depends on both).
 */
export interface CustomFieldFilterMeta {
  // No synthetic column ever exists for an ENCRYPTED definition (mergeCustomFieldColumns filters
  // it out before this map is built), so this excludes it — the compiler rejects an encrypted
  // filter target at every call site instead of a runtime check catching it here.
  readonly type: Exclude<CustomFieldValueType, 'encrypted'>;
  /** select/multiselect ONLY — client-side term→optionId(s) resolver. */
  readonly resolveOptionIds?: (term: string) => string[];
}

const VALUE_COLUMN_BY_TYPE: Record<
  Exclude<CustomFieldValueType, 'encrypted'>,
  CustomFieldValueColumn
> = {
  text: 'valueText',
  number: 'valueNumber',
  date: 'valueDate',
  boolean: 'valueBoolean',
  select: 'valueOptionId',
  multiselect: 'valueOptionIds',
};

/**
 * Translates a raw `ColumnFilter` produced by the generic filter UI
 * (`TableFilters`, a free-text operator + value box with no per-type
 * awareness) into the `CustomFieldFilter` descriptor `buildFilterWhere`
 * compiles into an EXISTS predicate over `customFieldValues` — but ONLY
 * when `filter.column` is a synthetic custom-field column id
 * (`` `customField.${definitionId}` ``, see `CUSTOM_FIELD_COLUMN_PREFIX`).
 * A filter on a plain/relation column is returned unchanged.
 *
 * - **select/multiselect**: the typed value column never stores the raw
 *   text the user typed — it stores `optionId`(s). The term is resolved
 *   CLIENT-SIDE via `meta.resolveOptionIds` (design.md D4 F1: never a
 *   server-side join to `option.search_text`). A term matching no option
 *   returns `undefined` — the caller drops the filter entirely rather than
 *   emitting an always-false condition.
 * - **boolean**: the UI's free-text value is coerced with `=== 'true'`.
 * - **number**: the UI's free-text value is coerced with `Number(...)`; the
 *   operator vocabulary (`equals`/`greaterThan`/`lessThan`) maps the same
 *   way `toFilterLeaf` maps it for a plain numeric column.
 * - **text/date**: delegates to `toFilterLeaf` unchanged (an `iLike`/`eq`
 *   condition is valid for both).
 *
 * @param filter - The raw filter as emitted by `TableFilters`.
 * @param definitions - Metadata for every synthetic column currently in
 *   scope, keyed by `definitionId`. A filter naming an unknown definitionId
 *   (should not happen — the id comes from a column the fusion already
 *   scoped) is returned unchanged as a defensive fallback.
 */
export function resolveCustomFieldFilter(
  filter: ColumnFilter,
  definitions: ReadonlyMap<string, CustomFieldFilterMeta>,
): ColumnFilter | CustomFieldFilter | undefined {
  if (!filter.column.startsWith(CUSTOM_FIELD_COLUMN_PREFIX)) return filter;

  const definitionId = filter.column.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
  const meta = definitions.get(definitionId);
  if (!meta) return filter;

  if (meta.type === 'select' || meta.type === 'multiselect') {
    const optionIds = meta.resolveOptionIds?.(filter.value) ?? [];
    if (optionIds.length === 0) return undefined;
    const condition: WhereLeaf =
      meta.type === 'select' ? { in: optionIds } : { overlap: optionIds };
    return {
      kind: 'customField',
      definitionId,
      valueColumn: VALUE_COLUMN_BY_TYPE[meta.type],
      condition,
    };
  }

  if (meta.type === 'boolean') {
    return {
      kind: 'customField',
      definitionId,
      valueColumn: 'valueBoolean',
      condition: { eq: filter.value === 'true' },
    };
  }

  const condition =
    meta.type === 'number'
      ? toNumericLeaf(filter.operator, filter.value)
      : toFilterLeaf(filter.operator, filter.value);
  return {
    kind: 'customField',
    definitionId,
    valueColumn: VALUE_COLUMN_BY_TYPE[meta.type],
    condition,
  };
}

function toNumericLeaf(operator: string, value: string): WhereLeaf {
  const num = Number(value);
  switch (operator) {
    case 'greaterThan':
      return { gt: num };
    case 'lessThan':
      return { lt: num };
    default:
      return { eq: num };
  }
}
